/**
 * author: Isaac Astley (Squiz)
 * email: iastley@squiz.net
 */
export default class Autocomplete {
    constructor({ data, input, resultsOnFocus, filterBy, listToggle }) {
        // static
        const sanitizedData = [];

        data.forEach((item) => {
            const current = item;

            current[filterBy] = item[filterBy].replace(/&amp;/gi, '&');

            sanitizedData.push(current);
        });

        this.data = sanitizedData;

        this.input =
            input instanceof NodeList || input instanceof Array
                ? [...input]
                : [...document.querySelectorAll(input)];
        this.results = this.data;
        this.resultsOnFocus = resultsOnFocus;
        this.filterBy = filterBy;
        this.listToggle = listToggle;

        // dynamic
        this.optionsOpened = null; // contains the currently opened options list
        this.optionIndex = 0; // the currently selected option (for _navigateResults event)
        this.selectedResults = []; // what results the user has selected (prevent duplicates)
        this.toggles = []; // all buttons that open the options list
        this.wrappers = []; // all of the input wrappers
        // this.dataTitles = this.data.map((item) => item[this.filterBy]); // isn't used - commented it out

        // kick off if there are inputs & data
        if (this.input.length && this.data) {
            // aphabetise the results
            this.data = this.data.sort((a, b) => {
                const aTitle = a[this.filterBy].toUpperCase();
                const bTitle = b[this.filterBy].toUpperCase();

                return aTitle.localeCompare(bTitle);
            });

            // if the input has values, add them to the selection
            this.input.forEach((item, i) => {
                if (item.value) this.selectedResults[i] = item.value;
            });

            this.optionList = document.createElement('div');

            this.optionList.classList.add('auto-completion__list');

            this._setResults();
            this._build();

            document.addEventListener('click', this._globalClick);
            document.addEventListener('keyup', this._globalClick);
        }
    }

    /// //////////////////////////////////////
    // -- public

    /**
     * prompts the check of what current values exist in the
     * chosen input(s)
     *
     * @return {void}
     */
    refreshInputs() {
        this.input.forEach((input, i) => {
            if (input.value) this.selectedResults[i] = input.value;
        });
    }

    /// //////////////////////////////////////
    // -- private

    /**
     * sets the result output with a list of results
     *
     * @return {void}
     */
    _setResults() {
        this.optionList.innerHTML = '';

        this.results.map((item, i) => {
            const listItem = document.createElement('button');

            listItem.classList.add('auto-completion__list-item');
            listItem.textContent = item[this.filterBy].replace(/&amp;/g, '&');
            listItem.dataset.id = i;
            listItem.dataset.value = item[this.filterBy].replace(/&amp;/g, '&');
            listItem.setAttribute(
                'aria-label',
                item[this.filterBy].replace(/&amp;/g, '&')
            );
            listItem.dataset.role = 'option-item';

            listItem.addEventListener('click', this._selectResult);
            listItem.addEventListener('keydown', this._selectResult);

            return this.optionList.appendChild(listItem);
        });
    }

    /**
     * builds out the main structure for the autocomplete
     * input it does this by wrapping the input in a <div>
     * then binds three events to it
     *
     * @return {void}
     */
    _build() {
        this.input.forEach((query, i) => {
            const wrapper = document.createElement('div');
            const typeahead = query;

            wrapper.classList.add('auto-completion');
            wrapper.dataset.id = i;

            this.wrappers.push(wrapper);

            typeahead.dataset.id = i;

            typeahead.after(wrapper);
            wrapper.append(typeahead);

            if (!this.listToggle) {
                typeahead.classList.add('auto-completion__query-no-toggle');
            }

            if (this.listToggle) {
                const button = document.createElement('button');

                typeahead.classList.add('auto-completion__query-has-toggle');

                button.innerHTML = `
                    <svg xmlns="http://www.w3.org/2000/svg" width="12" height="8" viewBox="0 0 12 8" fill="none">
                        <path fill-rule="evenodd" clip-rule="evenodd" d="M7.5 6.26917L6 7.78198L4.5 6.26917L2.64509e-07 1.73071L1.5 0.217896L6 4.75635L10.5 0.217896L12 1.73071L7.5 6.26917Z" fill="#060D87"/>
                    </svg>
                `;

                button.classList.add('auto-completion__options-toggle');
                button.type = 'button';
                button.name = 'toggle-search-suggestions';
                button.dataset.id = i;
                button.dataset.role = 'toggle-search-options';
                button.setAttribute(
                    'aria-label',
                    'toggle search suggestions box'
                );
                button.tabIndex = 0;

                this.toggles.push(button);

                wrapper.append(button);

                button.addEventListener(
                    'click',
                    this._searchListToggle.bind(this, i)
                );
            }

            if (this.resultsOnFocus) {
                query.setAttribute(
                    'aria-label',
                    `${query.name} search suggestions box opened.`
                );
            }

            wrapper.addEventListener('click', this._clicked.bind(this, i));
            wrapper.addEventListener(
                'keydown',
                this._navigateResults.bind(this, i)
            );
            wrapper.addEventListener(
                'keyup',
                this._navigateResults.bind(this, i)
            );
            query.addEventListener('focus', this._clicked.bind(this, i));
            query.addEventListener('keyup', this._search.bind(this, i));
            query.addEventListener('blur', this._blur.bind(this, i));
        });
    }

    /**
     * displays the options under the selected and current input
     *
     * @param {bool} isVisible
     * if this is set to true it will display the
     * results wrapper if no value is set or is false
     * the results wrapper is removed
     *
     * @return {void}
     */
    _options(isVisible) {
        /* eslint-disable no-unused-expressions */
        if (isVisible) {
            this.optionsOpened && this.optionsOpened.remove();

            const wrapper = document.createElement('div');

            wrapper.classList.add('auto-completion__options');

            wrapper.append(this.optionList);

            this.currentWrapper =
                this.wrappers[Number(this.current.dataset.id)];
            this.optionsOpened = wrapper;
            this.current.after(wrapper);

            return;
        }

        if (this.optionsOpened) {
            this.optionIndex = 0;
            this.optionsOpened.remove();
            this.optionsOpened = null;
            this.currentWrapper = null;
        }
    }

    /**
     * does an array includes with a regular expression
     * for more lenient searches
     *
     * @param {array} haystack
     * the array to find an element within
     *
     * @param {string|RegExp} needle
     * the value to find in an array
     *
     * @return {bool}
     */
    _includes(haystack, needle) {
        for (let i = 0; i < haystack.length; i++) {
            if (haystack[i] && haystack[i].match(needle)) return true;
        }

        return false;
    }

    /**
     * refines the search results and excludes
     * any searched term from the suggestions
     *
     * @param {string} searchTerm
     * the value to find in search results
     *
     * @return {void}
     */
    _refineSearch(searchTerm) {
        if (searchTerm) {
            const valueReg = new RegExp(`${this._quote(searchTerm)}`, 'img');
            const searchWord = new RegExp(
                `^${this._quote(searchTerm)}$`,
                'img'
            );

            this.results = this.data.filter((item) =>
                item[this.filterBy].match(valueReg)
            );

            if (
                this.results.length === 1 &&
                this.results[0][this.filterBy].match(searchWord)
            ) {
                this.results = this.data;
            }
        }

        this.results = this.results.filter((item) => {
            const selectedReg = new RegExp(
                `^${this._quote(item[this.filterBy])}$`,
                'img'
            );

            if (!this._includes(this.selectedResults, selectedReg)) {
                return item;
            }

            return false;
        });
    }

    /**
     * flattens all search result data to just
     * a string based array
     *
     * @return {array}
     */
    _flattenData() {
        const results = [];

        this.data.forEach((result) => {
            results.push(result[this.filterBy]);
        });

        return results;
    }

    /**
     * quotes regular expression sensitive
     * characters
     *
     * @param {string} string
     * the string to quote characters from
     *
     * @return {string}
     */
    _quote(string) {
        let quoted = string;

        quoted = quoted.replace(/\(/g, '\\(');
        quoted = quoted.replace(/\)/g, '\\)');
        quoted = quoted.replace(/\[/g, '\\[');
        quoted = quoted.replace(/\]/g, '\\]');
        quoted = quoted.replace(/\./g, '\\.');
        quoted = quoted.replace(/\?/g, '\\?');
        quoted = quoted.replace(/\*/g, '\\*');

        return quoted;
    }

    /// /////////////////////////////////////
    // -- events

    /**
     * validate the value of the search query on blur
     */
    _blur = (index, { target }) => {
        const { value } = target;
        const input = target;
        const valueReg = new RegExp(`^${this._quote(value)}$`, 'i');
        const dataHasValue = this._includes(this._flattenData(), valueReg);

        this._refineSearch(value);

        // gatekeey the value to prevent inappropriate values
        if ((!this.results.length && value) || (!dataHasValue && value)) {
            input.value = 'Other';
        }
    };

    /**
     * this event is bound to the document, it will
     * remove the result wrapper (created by _options) and
     * reset the results
     */
    _globalClick = ({ target, type, key }) => {
        if (!this.currentWrapper) return;

        switch (type) {
            case 'click':
                if (
                    this.currentWrapper &&
                    ![
                        ...this.currentWrapper.querySelectorAll('*'),
                        this.currentWrapper,
                    ].includes(target)
                ) {
                    this.optionIndex = 0;
                    this.results = this.data;
                    this._options();
                    this.currentWrapper = null;
                }
                break;
            case 'keyup':
                if (key && key.match(/escape|enter/i) && this.currentWrapper) {
                    this.optionIndex = 0;
                    this.results = this.data;
                    this._options();
                    this.currentWrapper = null;
                    this.current.focus();
                }
                break;
            default:
            // Do nothing
        }
    };

    /**
     * this event is bound to the input's wrapper (created by _build)
     * and will display the options wrapper
     */
    _clicked = (index, e) => {
        const { currentTarget, target, type } = e;

        if (target.dataset.role === 'toggle-search-options') return;

        e.preventDefault();

        this.current = this.input[index];

        if (type === 'focus') {
            this.current.click();
        }

        const valueReg = new RegExp(`${this.current.value}`, 'img');

        this._refineSearch();

        this.results = this.results.length
            ? this.results
            : [{ [this.filterBy]: 'Other' }];

        this._setResults();

        if (this.resultsOnFocus && type !== 'focus') {
            this._options(true);
        }
    };

    /**
     * toggles the options list via a button
     * next to the search input
     */
    _searchListToggle = (index, e) => {
        const { currentTarget } = e;

        if (!this.optionsOpened) {
            this.current = this.input[index];
            this._options(true);
            this.wrappers[index].querySelector('button').focus();

            return;
        }

        this._options();
    };

    /**
     * this event performs a filter on the results from the data
     * fed into the class based on what is typed into the current
     * search query
     */
    _search = (index, { target, key }) => {
        const valueReg = new RegExp(`${target.value}`, 'img');

        this.selectedResults[index] = target.value;

        this.results = this.data.filter((item) =>
            item[this.filterBy].match(valueReg)
        );

        this._refineSearch(target.value);

        this.results = this.results.length
            ? this.results
            : [{ [this.filterBy]: 'Other' }];

        this._setResults();

        if (target.value) {
            this._options(true);
        }
        // if (this.resultsOnFocus) this._options(true);
    };

    /**
     * interacts with the search result items when clicking
     * or pressing enter on an item, it will input its
     * value into the current search query
     */
    _selectResult = ({ type, key, target }) => {
        const { value } = target.dataset;

        switch (type) {
            case 'click':
                this.optionIndex = 0;
                this.current.value = value;
                this.selectedResults[Number(this.current.dataset.id)] =
                    this.current.value;
                break;
            case 'keydown':
                if (key.match(/enter/i)) {
                    this.optionIndex = 0;
                    this.current.value = value;
                    this.selectedResults[Number(this.current.dataset.id)] =
                        this.current.value;
                    this.current.focus();
                }
                break;
            default:
            // Do nothing
        }
    };

    /**
     * navigate through options via the keyboard arrow
     * keys
     */
    _navigateResults = (index, e) => {
        const { key, target, type } = e;

        if (!key || !this.currentWrapper) return;

        const items = [...this.currentWrapper.querySelectorAll('button')];

        if (type === 'keyup') {
            if (key.match(/tab/i) && target.dataset.role === 'option-item') {
                this.optionIndex = Number(target.dataset.id);
            }

            if (key.match(/arrowdown/i)) {
                e.preventDefault();

                if (target !== this.current) this.optionIndex += 1;
                if (!items[this.optionIndex]) this.optionIndex = 0;

                if (items[this.optionIndex]) items[this.optionIndex].focus();
            } else if (key.match(/arrowup/i)) {
                e.preventDefault();

                this.optionIndex =
                    target !== this.current
                        ? (this.optionIndex -= 1)
                        : (this.optionIndex = items.length - 1);

                if (!items[this.optionIndex])
                    this.optionIndex = items.length - 1;

                if (items[this.optionIndex]) items[this.optionIndex].focus();
            } else if (key.match(/ /i)) {
                if (target.dataset.value) {
                    this.input[index].value = target.dataset.value;
                    this.input[index].focus();
                }

                this._options();
            }
        }

        if (type === 'keydown') {
            if (key.match(/enter| /i) && !this.input.includes(target)) {
                e.preventDefault();
            }
            // if (key.match(/ /i)) {
            // this.input[index].value = target.dataset.value;
            // this.input[index].focus();
            // this._options();
            // }
        }
    };
}
